/**
 * Signed-out / signed-in authentication states for the central Cognitory panel.
 * Cognitory access resolution and answering live in CognitoryEntryExperience.
 */

import { useCognitoryAuth } from "../auth/useCognitoryAuth";
import {
  MAIN_PAGE_BODY_STYLE,
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
  MAIN_PAGE_LIQUID_BUTTON_STYLE,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
  MAIN_PAGE_RADIUS,
  mainPageLiquidButtonHandlers,
} from "../theme/mainPageVisual";
import CognitoryEntryExperience from "./CognitoryEntryExperience";
import { clearAllCognitoryAskThreads } from "../entry/cognitoryAskThreadSession";

export default function CognitoryAuthPanel() {
  const {
    status,
    email,
    diagnostics,
    errorMessage,
    signIn,
    signOutUser,
    clearError,
  } = useCognitoryAuth();

  if (status === "loading") {
    return (
      <div
        className="animate-fade-in animate-delay-5 cognitory-auth-panel"
        data-testid="cognitory-auth-loading"
        style={MAIN_PAGE_BODY_STYLE}
      >
        <p
          style={{
            fontSize: "1rem",
            marginBottom: 0,
            color: MAIN_PAGE_COLORS.soft,
          }}
        >
          Checking sign-in status…
        </p>
      </div>
    );
  }

  if (status === "signed_in" && diagnostics) {
    return (
      <div
        className="animate-fade-in animate-delay-5 cognitory-auth-panel"
        data-testid="cognitory-auth-signed-in"
        data-cognitory-visual-shell="main-page"
        style={MAIN_PAGE_BODY_STYLE}
      >
        <p
          data-testid="cognitory-auth-signed-in-line"
          style={{
            fontSize: "0.95rem",
            marginBottom: "0.35rem",
            color: MAIN_PAGE_COLORS.slate,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          Signed in as{" "}
          <span data-testid="cognitory-auth-email">{email || "unknown"}</span>
        </p>
        <CognitoryEntryExperience />
        <button
          type="button"
          data-testid="cognitory-auth-sign-out"
          style={MAIN_PAGE_QUIET_BUTTON_STYLE}
          onClick={() => {
            clearAllCognitoryAskThreads();
            void signOutUser();
          }}
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div
      className="animate-fade-in animate-delay-5 cognitory-auth-panel"
      data-testid="cognitory-auth-signed-out"
      data-cognitory-visual-shell="main-page"
      style={MAIN_PAGE_BODY_STYLE}
    >
      <p
        style={{
          fontSize: "1rem",
          marginBottom: "0.55rem",
          color: MAIN_PAGE_COLORS.slate,
          fontFamily: MAIN_PAGE_FONT_STACK,
        }}
      >
        Use Google to sign in with your approved account.
      </p>
      <p
        style={{
          fontSize: "0.95rem",
          marginBottom: 0,
          color: MAIN_PAGE_COLORS.soft,
          fontFamily: MAIN_PAGE_FONT_STACK,
        }}
      >
        After you sign in, Cognitory opens only the workspaces your account is
        authorized to use.
      </p>
      {errorMessage ? (
        <p
          data-testid="cognitory-auth-error"
          role="alert"
          style={{
            color: MAIN_PAGE_COLORS.alertInk,
            fontSize: "0.95rem",
            marginTop: "0.9rem",
            padding: "0.65rem 0.75rem",
            borderRadius: MAIN_PAGE_RADIUS.alert,
            background: MAIN_PAGE_COLORS.alertFill,
            border: `1px solid ${MAIN_PAGE_COLORS.alertBorder}`,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          {errorMessage}{" "}
          <button
            type="button"
            onClick={clearError}
            style={{
              border: "none",
              background: "transparent",
              color: MAIN_PAGE_COLORS.link,
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0,
              fontFamily: MAIN_PAGE_FONT_STACK,
            }}
          >
            Dismiss
          </button>
        </p>
      ) : null}
      <button
        type="button"
        data-testid="cognitory-auth-sign-in"
        className="cognitory-auth-liquid-btn"
        aria-label="Sign in with Google"
        style={MAIN_PAGE_LIQUID_BUTTON_STYLE}
        {...mainPageLiquidButtonHandlers}
        onClick={() => {
          void signIn();
        }}
      >
        Sign in with Google
      </button>
    </div>
  );
}
